import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/cn';
import { useCachedAccess } from '@/lib/access/use-access';
import { canChangeMode, effectiveMode, IntegrationModeError, modeChoices } from '@/lib/providers/binding-mode';
import { integrationKey, vendorLabel } from '@/lib/providers/integration-state';
import { TOGGLE_REASON_MAX } from '@/lib/providers/integration-toggle';
import type { IntegrationDescriptor, IntegrationMode, IntegrationModeErrorCode } from '@/lib/providers/types';
import { useIntegrationMode } from '@/lib/providers/use-providers';
import { DetailItem, ModeChip } from './provider-table';
import { MODE_TONE } from './mode-tone';
import { formatWhen, modeWarning } from './format';

/**
 * M2 (D14–D16) — the MODE of one binding, in two pieces:
 *
 *  · {@link ModeCell} — the "mode" column. When the declaring app routes each
 *    call by the binding's mode (`selectableModes` holds two or more), a
 *    segmented choice limited to exactly those modes: Real · Sandbox ·
 *    Sintético. Otherwise the mode chip and «fijo».
 *  · {@link IntegrationModeFacts} / {@link IntegrationModeNote} — in the row
 *    detail: the mode in use, the boot mode, what may be chosen, and the
 *    operator decision in force (who, when, why) — saying plainly when that
 *    decision is IGNORED and the boot mode is serving.
 *
 * Like the on/off switch, and under the same PERMISSION (D16, `providers.toggle`
 * — read from the access the console already loaded, never fetched per row): a
 * choice opens a dialog that requires a reason; nothing moves until the BFF
 * answers with the committed row; a refusal (incl. a 403) is explained in words.
 * Without the permission the choice is read-only and says so. The active segment wears the
 * mode chip's colours, so a synthetic binding stays violet and a real one red.
 */

/** Copy for a refused or failed write. Codes the switch already explains reuse its words. */
const ERROR_KEY: Record<IntegrationModeErrorCode, string> = {
  not_found: 'providers.integrations.toggle.error.unknown_vendor',
  not_mode_selectable: 'providers.mode.error.not_mode_selectable',
  mode_not_selectable: 'providers.mode.error.mode_not_selectable',
  mode_not_allowed: 'providers.mode.error.mode_not_allowed',
  reason_required: 'providers.integrations.toggle.dialog.reasonRequired',
  conflict: 'providers.integrations.toggle.error.version_conflict',
  forbidden: 'providers.mode.error.forbidden',
  invalid_request: 'providers.integrations.toggle.error.invalid_request',
  unavailable: 'providers.integrations.toggle.error.unavailable',
};

/**
 * M3 (I3, `integrations-tds` §15.7): the declaring app BOOTED in a mode this
 * deployment kind does not allow (`bootModeForbidden`). Only an older build can
 * do that, since every current service refuses such a boot. The row says so on
 * its face. The chip is NOT relabelled: it still shows the mode the app uses.
 */
export function BootModeForbiddenNotice({ descriptor }: { descriptor: IntegrationDescriptor }) {
  const { t } = useTranslation('network');
  return (
    <p
      className="flex items-start gap-1.5 text-xs font-medium text-amber-700 dark:text-amber-400"
      title={t('providers.mode.bootForbiddenHint')}
      data-testid={`integration-boot-forbidden-${integrationKey(descriptor)}`}
    >
      <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
      {t('providers.mode.bootForbidden', { mode: t(`providers.mode.name.${descriptor.mode}`) })}
    </p>
  );
}

/** A mode that cannot be chosen here: the chip, and «fijo» under it. */
export function FixedMode({
  mode,
  category,
  testId,
  fixedTestId,
}: {
  mode: IntegrationMode | null;
  /** The binding's service — the chip's hover says what this mode means for it. */
  category: string;
  testId?: string;
  fixedTestId?: string;
}) {
  const { t } = useTranslation('network');
  return (
    <div className="flex flex-col items-start gap-0.5">
      <ModeChip mode={mode} category={category} {...(testId ? { testId } : {})} />
      {mode ? (
        <span className="text-[11px] text-muted-foreground" title={t('providers.mode.fixedHint')} data-testid={fixedTestId}>
          {t('providers.mode.fixed')}
        </span>
      ) : null}
    </div>
  );
}

export function ModeCell({ descriptor }: { descriptor: IntegrationDescriptor }) {
  const { t } = useTranslation('network');
  const key = integrationKey(descriptor);
  const choices = modeChoices(descriptor);
  const current = effectiveMode(descriptor);
  const [target, setTarget] = useState<IntegrationMode | null>(null);
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const mutation = useIntegrationMode(descriptor);
  const access = useCachedAccess();

  const modeName = (mode: IntegrationMode) => t(`providers.mode.name.${mode}`);
  const override = descriptor.modeOverride;
  // Said in the cell too: the operator's choice is NOT what serves right now.
  const ignored = override?.ignored ? (
    <span
      className="flex items-center gap-1 text-[11px] text-amber-700 dark:text-amber-400"
      data-testid={`integration-mode-ignored-${key}`}
    >
      <TriangleAlert className="h-3 w-3 shrink-0" aria-hidden />
      {t('providers.mode.ignored', { mode: modeName(override.mode) })}
    </span>
  ) : null;

  if (choices.length === 0) {
    return (
      <div className="flex flex-col items-start gap-0.5" data-testid={`integration-mode-cell-${key}`} data-state="fixed">
        <FixedMode
          mode={current}
          category={descriptor.category}
          testId={`integration-mode-${key}`}
          fixedTestId={`integration-mode-fixed-${key}`}
        />
        {ignored}
      </div>
    );
  }

  const name = vendorLabel(t, descriptor.category, descriptor.vendor);
  const allowed = canChangeMode(access);
  const trimmed = reason.trim();
  const errorCode: IntegrationModeErrorCode | null =
    mutation.error instanceof IntegrationModeError ? mutation.error.code : mutation.error ? 'unavailable' : null;
  const state = mutation.isPending ? 'pending' : allowed ? 'selectable' : 'locked';

  const choose = (mode: IntegrationMode): void => {
    if (mode === current) return;
    mutation.reset();
    setReason('');
    setTouched(false);
    setTarget(mode);
  };

  const confirm = (): void => {
    setTouched(true);
    if (!target || trimmed.length === 0) return;
    mutation.mutate({ mode: target, reason: trimmed }, { onSuccess: () => setTarget(null) });
  };

  return (
    <div className="flex flex-col items-start gap-0.5" data-testid={`integration-mode-cell-${key}`} data-state={state}>
      <div
        role="radiogroup"
        aria-label={t('providers.mode.groupLabel', { name })}
        className="inline-flex flex-wrap gap-0.5 rounded-md border border-border p-0.5"
        data-testid={`integration-mode-select-${key}`}
        data-mode={current}
      >
        {choices.map((mode) => {
          const on = mode === current;
          return (
            <button
              key={mode}
              type="button"
              role="radio"
              aria-checked={on}
              disabled={!allowed || mutation.isPending}
              onClick={() => choose(mode)}
              data-testid={`integration-mode-option-${key}-${mode}`}
              className={cn(
                'inline-flex min-h-11 items-center rounded border px-1.5 text-[11px] font-semibold',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                'disabled:cursor-not-allowed',
                on
                  ? MODE_TONE[mode]
                  : 'border-transparent text-muted-foreground enabled:hover:bg-accent enabled:hover:text-accent-foreground disabled:opacity-60',
              )}
            >
              {modeName(mode)}
            </button>
          );
        })}
      </div>
      {allowed ? null : (
        <span className="text-[11px] text-muted-foreground" data-testid={`integration-mode-locked-${key}`}>
          {t('providers.mode.locked')}
        </span>
      )}
      {ignored}

      <Dialog open={target !== null} onOpenChange={(next) => (mutation.isPending || next ? undefined : setTarget(null))}>
        <DialogContent data-testid={`integration-mode-dialog-${key}`} data-target={target ?? undefined}>
          <DialogHeader>
            <DialogTitle>{target ? t('providers.mode.dialog.title', { name, mode: modeName(target) }) : null}</DialogTitle>
            <DialogDescription>{t('providers.mode.dialog.body')}</DialogDescription>
          </DialogHeader>
          {target ? (
            <p
              className={cn('rounded-md border px-3 py-2 text-xs font-medium', MODE_TONE[target])}
              data-testid={`integration-mode-dialog-hint-${key}`}
            >
              {modeWarning(t, target, descriptor.category)}
            </p>
          ) : null}

          <label className="block text-sm">
            <span className="mb-1 block font-medium">{t('providers.integrations.toggle.dialog.reasonLabel')}</span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={TOGGLE_REASON_MAX}
              rows={3}
              placeholder={t('providers.integrations.toggle.dialog.reasonPlaceholder')}
              className="w-full rounded-md border border-input bg-background p-2 text-sm"
              data-testid={`integration-mode-reason-${key}`}
            />
          </label>
          {touched && trimmed.length === 0 ? (
            <p className="text-xs text-destructive" data-testid={`integration-mode-reason-required-${key}`}>
              {t('providers.integrations.toggle.dialog.reasonRequired')}
            </p>
          ) : null}
          {errorCode ? (
            <p
              className="text-xs text-destructive"
              role="alert"
              data-testid={`integration-mode-error-${key}`}
              data-code={errorCode}
              data-state={errorCode === 'unavailable' ? 'failed' : 'refused'}
            >
              {t(ERROR_KEY[errorCode])}
            </p>
          ) : null}

          <DialogFooter>
            <Button type="button" variant="ghost" disabled={mutation.isPending} onClick={() => setTarget(null)}>
              {t('providers.integrations.toggle.dialog.cancel')}
            </Button>
            <Button
              type="button"
              variant={target === 'production' ? 'destructive' : 'default'}
              disabled={mutation.isPending}
              onClick={confirm}
              data-testid={`integration-mode-confirm-${key}`}
            >
              {mutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : null}
              {mutation.isPending
                ? t('providers.integrations.toggle.dialog.saving')
                : target
                  ? t('providers.mode.dialog.confirm', { mode: modeName(target) })
                  : null}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** The detail's facts about the mode: in use, at boot (when different), what may be chosen. */
export function IntegrationModeFacts({ descriptor }: { descriptor: IntegrationDescriptor }) {
  const { t } = useTranslation('network');
  const key = integrationKey(descriptor);
  const current = effectiveMode(descriptor);
  const choices = modeChoices(descriptor);
  const name = (mode: IntegrationMode) => t(`providers.mode.name.${mode}`);
  return (
    <>
      <DetailItem label={t('providers.mode.inUse')} testId={`integration-mode-in-use-${key}`}>
        {name(current)}
      </DetailItem>
      {current !== descriptor.mode ? (
        <DetailItem label={t('providers.mode.boot')} testId={`integration-mode-boot-${key}`}>
          {name(descriptor.mode)}
        </DetailItem>
      ) : null}
      <DetailItem label={t('providers.mode.choices')} testId={`integration-mode-choices-${key}`}>
        {choices.length ? choices.map(name).join(' · ') : t('providers.inUseCell.fixed')}
      </DetailItem>
    </>
  );
}

/**
 * The operator's mode decision in force — who, when, in their words why — and,
 * when the control plane does not serve it, a plain sentence saying so.
 */
export function IntegrationModeNote({ descriptor }: { descriptor: IntegrationDescriptor }) {
  const { t, i18n } = useTranslation('network');
  const override = descriptor.modeOverride;
  if (!override) return null;
  const key = integrationKey(descriptor);
  const name = (mode: IntegrationMode) => t(`providers.mode.name.${mode}`);
  return (
    <div className="space-y-1 text-xs">
      <p className="text-muted-foreground" data-testid={`integration-mode-override-${key}`}>
        {t('providers.mode.override', {
          mode: name(override.mode),
          who: override.byName ?? override.byUrn,
          when: formatWhen(override.at, i18n.language),
        })}
        {override.reason ? <span className="block italic">“{override.reason}”</span> : null}
      </p>
      {override.ignored ? (
        <p
          className="flex items-start gap-1 font-medium text-amber-700 dark:text-amber-400"
          role="status"
          data-testid={`integration-mode-ignored-detail-${key}`}
        >
          <TriangleAlert className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
          {t('providers.mode.ignoredDetail', { mode: name(override.mode), boot: name(descriptor.mode) })}
        </p>
      ) : null}
    </div>
  );
}
